import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { SentryErrorBoundary, sentryInitialized } from "@/sentry";
import { posthogInitialized } from "@/posthog";

/**
 * Self-test trigger for Sentry — gated by URL param `?__sentry_self_test=m1-sentry-validation`.
 * Throws a controlled error so Sentry captures it via the ErrorBoundary.
 * Safe to leave in place: triggered only by exact secret token.
 */
function MaybeSelfTest({ children }) {
  const params = new URLSearchParams(window.location.search);
  if (params.get("__sentry_self_test") === "m1-sentry-validation") {
    throw new Error("Sentry self-test (Frontend) — Milestone 1.2 verification. Safe to ignore.");
  }
  return children;
}

function ErrorFallback({ error }) {
  return (
    <div style={{ padding: 24, fontFamily: "sans-serif" }}>
      <h2>Something went wrong.</h2>
      <p style={{ color: "#888" }}>The error has been reported. Please try again.</p>
      <pre style={{ background: "#f7f7f7", padding: 12, borderRadius: 6, fontSize: 12 }}>
        {String(error?.message || error)}
      </pre>
    </div>
  );
}

function Root() {
  useEffect(() => {
    if (sentryInitialized()) {
      // eslint-disable-next-line no-console
      console.info("[Sentry] active on this page");
    }
    if (posthogInitialized()) {
      // eslint-disable-next-line no-console
      console.info("[PostHog] active on this page");
    }
  }, []);
  return (
    <SentryErrorBoundary fallback={ErrorFallback}>
      <MaybeSelfTest>
        <App />
      </MaybeSelfTest>
    </SentryErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      reg.update().catch(() => {});
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") reg.update().catch(() => {});
      });
      setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
    }).catch(() => {});
  });
}
